import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import { useTheme } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { App } from '@meronex/app';
import { toast } from '@meronex/components';

import IStores from '@meronex/icons/fi/FiPackage';
import IChevron from '@meronex/icons/fi/FiChevronRight';
import IChevronDown from '@meronex/icons/fi/FiChevronDown';
import IAdd from '@meronex/icons/fi/FiPlus';
import IArchive from '@meronex/icons/fi/FiArchive';
import ISwitch from '@meronex/icons/fi/FiSearch';
import IStoreItem from '@meronex/icons/fi/FiBox';
import IEdit from '@meronex/icons/fi/FiEdit2';
import IAudit from '@meronex/icons/fi/FiClipboard';
import IChart from '@meronex/icons/fi/FiBarChart2';
import ICsv from '@meronex/icons/fi/FiDownload';

import { Icons } from '../../common/Icons';
import { can, p } from '../../common/roles';
import { storesMng } from '../../stores/api/storesMng';
import AddStoreDialog from '../../stores/components/stores/AddStoreDialog';
import StoresDialog from '../../stores/components/stores/StoresDialog';
import ArchivedStoresDialog from '../../stores/components/stores/ArchivedStoresDialog';

export default function SideMenu(props) {
  const { activeView } = props;
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const [user, setUser] = React.useState(null);
  const [userLoaded, setUserLoaded] = React.useState(false);
  const [stores, setStores] = React.useState(null);
  const [storesExpanded, setStoresExpanded] = React.useState(true);
  const [selectedStoreId, setSelectedStoreId] = React.useState(null);
  const [openAddStoreDialog, setOpenAddStoreDialog] = React.useState(false);
  const [openStoresDialog, setOpenStoresDialog] = React.useState(false);
  const [openArchivedStoresDialog, setOpenArchivedStoresDialog] = React.useState(false);
  const [contextMenu, setContextMenu] = React.useState(null);
  const [archiveConfirm, setArchiveConfirm] = React.useState(null);
  const [archivingId, setArchivingId] = React.useState(null);

  // Close context menu on any click
  React.useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    document.addEventListener('click', close);
    document.addEventListener('contextmenu', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close);
    };
  }, [contextMenu]);

  React.useEffect(() => {
    setUser(App.getUser());
  }, [userLoaded]);

  const onUserLoaded = () => {
    setUserLoaded(true);
  };

  React.useEffect(() => {
    App.eventsManager.on('event-user-loaded', 'SideMenu', onUserLoaded);
    return () => {
      App.eventsManager.removeListener(
        'event-user-loaded',
        'SideMenu',
        onUserLoaded
      );
    };
  });

  React.useEffect(() => {
    fetchStores();
    App.eventsManager.on('event-connected', 'SideMenu-stores', () => {
      fetchStores();
    });
    App.eventsManager.on('event-sites-loaded', 'SideMenu-stores', () => {
      fetchStores();
    });
    App.eventsManager.on('event-store-selected', 'SideMenu-storeSelect', (e) => {
      if (e && e.storeId) {
        setSelectedStoreId(e.storeId);
      }
    });
    return () => {
      App.eventsManager.removeListener('event-store-selected', 'SideMenu-storeSelect');
    };
  }, []);

  const fetchStores = async () => {
    try {
      const result = await storesMng.getStores();
      const storesArray = Array.isArray(result) ? result : [];
      setStores(storesArray);
      // Pick up the saved selection if we don't have one yet
      if (!selectedStoreId && storesArray.length > 0) {
        const saved = await storesMng.getUserSelectedStore(storesArray);
        if (saved && saved._id) {
          setSelectedStoreId(saved._id);
        }
      }
    } catch (e) {
      setStores([]);
    }
  };

  const onStoreSelect = (store, cb) => {
    setActiveViewAndStore('stores', store._id);
    if (cb) cb();
  };

  const setActiveViewAndStore = (view, storeId) => {
    if (storeId) {
      storesMng.setUserSelectedStore(storeId);
      setSelectedStoreId(storeId);
    }
    App.eventsManager.emit('event-active-view', SideMenu.name, {
      activeView: view,
    });
    if (storeId) {
      App.eventsManager.emit('event-store-selected', SideMenu.name, {
        storeId,
      });
    }
    // Close mobile drawer on any navigation
    App.eventsManager.emit('event-close-mobile-menu', SideMenu.name);
  };

  const [refreshing, setRefreshing] = React.useState(false);

  const hardRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    App.forceReload();
  };

  const _onClick = (item) => {
    if (item._id) {
      if (item._id === 'refresh') {
        hardRefresh();
      } else {
        App.eventsManager.emit('event-active-view', SideMenu.name, {
          activeView: item._id,
        });
      }
      // Close mobile drawer on any navigation
      App.eventsManager.emit('event-close-mobile-menu', SideMenu.name);
    }
  };

  const activeColor = '#6366f1';
  const activeBg = isDark ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.06)';
  const mutedColor = isDark ? '#999' : '#666';

  const renderMenuItem = (item, index) => {
    const isActive = activeView === item._id;
    return (
      <div
        key={index}
        onClick={() => {
          _onClick(item);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderRadius: '8px',
          margin: '1px 8px',
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: isActive ? activeBg : 'transparent',
          transition: 'background-color 0.15s ease',
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = isDark
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(0,0,0,0.03)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isActive ? activeBg : 'transparent';
        }}
      >
        <span style={{
          color: isActive ? activeColor : mutedColor,
          display: 'flex',
          alignItems: 'center',
        }}>
          {item.icon}
        </span>
        <span style={{
          fontSize: '14px',
          fontWeight: isActive ? 600 : 400,
          color: isActive ? activeColor : 'inherit',
        }}>
          {item.label}
        </span>
      </div>
    );
  };

  const renderStoresTree = () => {
    if (!can(p.globals.accessStores)) return null;

    const isStoresActive = activeView === 'stores';
    const hasSelectedChild = isStoresActive && selectedStoreId;
    const isParentHighlighted = isStoresActive && !hasSelectedChild;
    const ChevronIcon = storesExpanded ? IChevronDown : IChevron;

    return (
      <div>
        {/* Stores header — clickable to navigate, chevron to expand/collapse */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: '8px',
            margin: '1px 8px',
            padding: '8px 12px',
            cursor: 'pointer',
            backgroundColor: isParentHighlighted ? activeBg : 'transparent',
            transition: 'background-color 0.15s ease',
          }}
          onClick={() => setActiveViewAndStore('stores')}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const menuW = 180, menuH = 100;
            const x = Math.min(e.clientX, window.innerWidth - menuW - 8);
            const y = Math.min(e.clientY, window.innerHeight - menuH - 8);
            setContextMenu({ x, y, store: null, isHeader: true });
          }}
          onMouseEnter={(e) => {
            if (!isParentHighlighted) {
              e.currentTarget.style.backgroundColor = isDark
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.03)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = isParentHighlighted ? activeBg : 'transparent';
          }}
        >
          <span style={{
            marginRight: '10px',
            color: isParentHighlighted ? activeColor : mutedColor,
            display: 'flex',
            alignItems: 'center',
          }}>
            <IStores size={20} />
          </span>
          <span style={{
            flex: 1,
            fontSize: '14px',
            fontWeight: isParentHighlighted ? 600 : 400,
            color: isParentHighlighted ? activeColor : 'inherit',
          }}>
            Stores
          </span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              setStoresExpanded(!storesExpanded);
            }}
            style={{
              color: mutedColor,
              display: 'flex',
              alignItems: 'center',
              padding: '2px',
              borderRadius: '4px',
            }}
          >
            <ChevronIcon size={18} />
          </span>
        </div>

        {/* Store list — shimmer while loading */}
        {storesExpanded && stores === null && (
          <div style={{ padding: '6px 0 6px 20px', marginLeft: '16px', borderLeft: `2px solid ${isDark ? '#444' : '#e2e8f0'}` }}>
            {[0, 0.1, 0.15, 0.2, 0.25].map((d, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 12px', margin: '1px 8px',
              }}>
                <div style={{
                  width: 14, height: 14, borderRadius: 4,
                  backgroundColor: isDark ? '#333' : '#e4e4e4',
                  animation: `skPulse 1.8s ease-in-out ${d}s infinite`,
                }} />
                <div style={{
                  flex: 1, height: 12, borderRadius: 4,
                  backgroundColor: isDark ? '#333' : '#e4e4e4',
                  animation: `skPulse 1.8s ease-in-out ${d + 0.05}s infinite`,
                  maxWidth: `${[75, 90, 60, 50, 70][i]}%`,
                }} />
              </div>
            ))}
            <style>{`
              @keyframes skPulse {
                0%, 100% { background-color: ${isDark ? '#333' : '#e4e4e4'}; }
                50% { background-color: ${isDark ? '#3e3e44' : '#f2f2f2'}; }
              }
            `}</style>
          </div>
        )}
        {/* Store list — nested under Stores */}
        {storesExpanded && stores && stores.length > 0 && (
          <div style={{ padding: '2px 0 2px 20px', marginLeft: '16px', borderLeft: `2px solid ${isDark ? '#444' : '#e2e8f0'}` }}>
            {[...stores].sort((a, b) => (b.itemCount || 0) - (a.itemCount || 0)).map((store) => {
              const isSelected = activeView === 'stores' && selectedStoreId === store._id;
              return (
              <div
                key={store._id}
                onClick={() => setActiveViewAndStore('stores', store._id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const menuW = 180, menuH = 200;
                  const x = Math.min(e.clientX, window.innerWidth - menuW - 8);
                  const y = Math.min(e.clientY, window.innerHeight - menuH - 8);
                  setContextMenu({ x, y, store });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  margin: '1px 8px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: isSelected ? activeColor : (isDark ? '#bbb' : '#555'),
                  fontWeight: isSelected ? 600 : 400,
                  backgroundColor: isSelected ? activeBg : 'transparent',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = isDark
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.03)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected ? activeBg : 'transparent';
                }}
              >
                {archivingId === store._id
                  ? <CircularProgress size={14} style={{ color: mutedColor }} />
                  : <IStoreItem size={14} style={{ opacity: 0.5 }} />
                }
                <span style={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {store.name}
                </span>
                <span style={{
                  fontSize: '11px',
                  color: isDark ? '#777' : '#aaa',
                  flexShrink: 0,
                }}>
                  {store.itemCount || 0}
                </span>
              </div>
            );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderManageSection = () => {
    if (!can(p.globals.accessStores)) return null;

    return (
      <div>
        <div style={{
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          color: isDark ? '#666' : '#94a3b8',
          padding: '16px 20px 4px',
        }}>
          Manage
        </div>
        {[
          { label: 'Add New Store', icon: <IAdd style={{ fontSize: 18 }} />, onClick: () => setOpenAddStoreDialog(true) },
          { label: 'Browse All Stores', icon: <ISwitch style={{ fontSize: 18 }} />, onClick: () => setOpenStoresDialog(true) },
          { label: 'Archived Stores', icon: <IArchive style={{ fontSize: 18 }} />, onClick: () => setOpenArchivedStoresDialog(true) },
        ].map((action) => (
          <div
            key={action.label}
            onClick={action.onClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '8px 12px',
              margin: '1px 8px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: isDark ? '#999' : '#64748b',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.03)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {action.icon}
            </span>
            <span>{action.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const skFrom = isDark ? '#333' : '#e4e4e4';
  const skTo = isDark ? '#3e3e44' : '#f2f2f2';

  if (!user && !userLoaded) {
    return (
      <div style={{ paddingRight: '15px', position: 'sticky', top: 0 }}>
        <div style={{
          backgroundColor: theme.palette.background.paper,
          padding: '10px 0',
        }}>
          <style>{`
            @keyframes skPulse {
              0%, 100% { background-color: ${skFrom}; }
              50% { background-color: ${skTo}; }
            }
          `}</style>
          {/* Home */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px', margin: '2px 8px' }}>
            <div style={{ width: 20, height: 20, borderRadius: 5, backgroundColor: skFrom, animation: 'skPulse 1.8s ease-in-out infinite' }} />
            <div style={{ width: 50, height: 13, borderRadius: 4, backgroundColor: skFrom, animation: 'skPulse 1.8s ease-in-out 0.05s infinite' }} />
          </div>
          {/* Stores header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px', margin: '2px 8px' }}>
            <div style={{ width: 20, height: 20, borderRadius: 5, backgroundColor: skFrom, animation: 'skPulse 1.8s ease-in-out 0.08s infinite' }} />
            <div style={{ width: 55, height: 13, borderRadius: 4, backgroundColor: skFrom, animation: 'skPulse 1.8s ease-in-out 0.1s infinite' }} />
          </div>
          {/* Store items */}
          <div style={{ padding: '2px 0 2px 20px' }}>
            {[0.12, 0.16, 0.2, 0.24, 0.28].map((d, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 12px', margin: '1px 8px',
              }}>
                <div style={{
                  width: 14, height: 14, borderRadius: 4,
                  backgroundColor: skFrom, animation: `skPulse 1.8s ease-in-out ${d}s infinite`,
                }} />
                <div style={{
                  flex: 1, height: 12, borderRadius: 4,
                  backgroundColor: skFrom, animation: `skPulse 1.8s ease-in-out ${d + 0.05}s infinite`,
                  maxWidth: `${[75, 90, 60, 50, 70][i]}%`,
                }} />
              </div>
            ))}
          </div>
          {/* Divider */}
          <div style={{ height: 1, margin: '6px 12px', backgroundColor: isDark ? '#333' : '#eee' }} />
          {/* Refresh + Settings */}
          {[0.3, 0.35].map((d, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px', margin: '2px 8px' }}>
              <div style={{ width: 20, height: 20, borderRadius: 5, backgroundColor: skFrom, animation: `skPulse 1.8s ease-in-out ${d}s infinite` }} />
              <div style={{ width: [55, 60][i], height: 13, borderRadius: 4, backgroundColor: skFrom, animation: `skPulse 1.8s ease-in-out ${d + 0.05}s infinite` }} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        paddingRight: '15px',
        position: 'sticky',
        top: '0',
      }}>
      <div
        style={{
          backgroundColor: theme.palette.background.paper,
          padding: '6px 0',
          overflow: 'hidden',
        }}>
        {renderMenuItem({ _id: 'home', label: 'Home', icon: <Icons.Home size={20} /> }, 'home')}
        {renderStoresTree()}
        {renderManageSection()}
        <div style={{
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          color: isDark ? '#666' : '#94a3b8',
          padding: '16px 20px 4px',
        }}>
          System
        </div>
        {renderMenuItem({
          _id: 'refresh',
          label: refreshing ? 'Refreshing...' : 'Refresh',
          icon: refreshing
            ? <CircularProgress size={18} style={{ color: mutedColor }} />
            : <Icons.Refresh size={20} />,
        }, 'refresh')}
        {can(p.globals.viewConfigurations) &&
          renderMenuItem({ _id: 'configurations', label: 'Admin', icon: <Icons.Settings size={20} /> }, 'admin')
        }
      </div>
      {contextMenu && ReactDOM.createPortal(
        (() => {
          const menuItemStyle = {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 14px',
            fontSize: '13px',
            cursor: 'pointer',
            color: isDark ? '#e2e8f0' : '#334155',
            whiteSpace: 'nowrap',
            transition: 'background-color 0.1s ease',
          };
          const dividerStyle = {
            height: '1px',
            backgroundColor: isDark ? '#333' : '#f1f5f9',
            margin: '4px 0',
          };
          const sectionLabelStyle = {
            fontSize: '10px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: isDark ? 'rgba(99,102,241,0.7)' : '#6366f1',
            padding: '6px 14px 2px',
          };
          const menuHover = (e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; };
          const menuLeave = (e) => { e.currentTarget.style.backgroundColor = 'transparent'; };
          return (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                left: contextMenu.x,
                top: contextMenu.y,
                zIndex: 9999,
                backgroundColor: isDark ? '#1e1e22' : '#fff',
                borderRadius: '10px',
                border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`,
                boxShadow: isDark
                  ? '0 8px 24px rgba(0,0,0,0.4)'
                  : '0 8px 24px rgba(0,0,0,0.12)',
                padding: '4px 0',
                minWidth: '170px',
              }}>
              {contextMenu.isHeader ? (
                <>
                  <div
                    style={menuItemStyle}
                    onClick={() => {
                      setContextMenu(null);
                      setOpenAddStoreDialog(true);
                    }}
                    onMouseEnter={menuHover}
                    onMouseLeave={menuLeave}>
                    <IAdd size={14} style={{ opacity: 0.6 }} />
                    <span>Add Store</span>
                  </div>
                  <div
                    style={menuItemStyle}
                    onClick={() => {
                      setContextMenu(null);
                      setOpenStoresDialog(true);
                    }}
                    onMouseEnter={menuHover}
                    onMouseLeave={menuLeave}>
                    <ISwitch size={14} style={{ opacity: 0.6 }} />
                    <span>Browse Stores</span>
                  </div>
                  <div
                    style={menuItemStyle}
                    onClick={() => {
                      setContextMenu(null);
                      setOpenArchivedStoresDialog(true);
                    }}
                    onMouseEnter={menuHover}
                    onMouseLeave={menuLeave}>
                    <IArchive size={14} style={{ opacity: 0.6 }} />
                    <span>Archived Stores</span>
                  </div>
                </>
              ) : (
                <>
                  {/* Reports */}
                  <div style={sectionLabelStyle}>Reports</div>
                  <div style={menuItemStyle} onClick={() => {
                    const storeId = contextMenu.store._id;
                    setContextMenu(null);
                    setActiveViewAndStore('stores', storeId);
                    setTimeout(() => {
                      App.eventsManager.emit('event-store-action', SideMenu.name, { storeId, action: 'analytics' });
                    }, 100);
                  }} onMouseEnter={menuHover} onMouseLeave={menuLeave}>
                    <IChart size={14} style={{ opacity: 0.6 }} />
                    <span>Store Analytics</span>
                  </div>
                  <div style={menuItemStyle} onClick={() => {
                    const storeId = contextMenu.store._id;
                    setContextMenu(null);
                    setActiveViewAndStore('stores', storeId);
                    setTimeout(() => {
                      App.eventsManager.emit('event-store-action', SideMenu.name, { storeId, action: 'exportCsv' });
                    }, 100);
                  }} onMouseEnter={menuHover} onMouseLeave={menuLeave}>
                    <ICsv size={14} style={{ opacity: 0.6 }} />
                    <span>Generate Reports</span>
                  </div>
                  <div style={dividerStyle} />
                  {/* Store */}
                  <div style={sectionLabelStyle}>Store</div>
                  {can(p.stores.auditStore) && (
                    <div style={menuItemStyle} onClick={() => {
                      const storeId = contextMenu.store._id;
                      setContextMenu(null);
                      setActiveViewAndStore('stores', storeId);
                      setTimeout(() => {
                        App.eventsManager.emit('event-audit-store', SideMenu.name, { storeId });
                      }, 100);
                    }} onMouseEnter={menuHover} onMouseLeave={menuLeave}>
                      <IAudit size={14} style={{ opacity: 0.6 }} />
                      <span>Audit Store</span>
                    </div>
                  )}
                  <div style={menuItemStyle} onClick={() => {
                    const storeId = contextMenu.store._id;
                    setContextMenu(null);
                    setActiveViewAndStore('stores', storeId);
                    setTimeout(() => {
                      App.eventsManager.emit('event-edit-store', SideMenu.name, { storeId });
                    }, 100);
                  }} onMouseEnter={menuHover} onMouseLeave={menuLeave}>
                    <IEdit size={14} style={{ opacity: 0.6 }} />
                    <span>Edit Store</span>
                  </div>
                  <div
                    style={{ ...menuItemStyle, color: isDark ? '#f87171' : '#dc2626' }}
                    onClick={() => {
                      const storeToArchive = contextMenu.store;
                      setContextMenu(null);
                      if (stores && stores.length <= 1) {
                        toast.error('You need at least one active store.', { autoClose: 900 });
                        return;
                      }
                      setArchiveConfirm(storeToArchive);
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.05)' : '#fef2f2'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                    <IArchive size={14} style={{ opacity: 0.6 }} />
                    <span>Archive Store</span>
                  </div>
                </>
              )}
            </div>
          );
        })(),
        document.body
      )}
      {openAddStoreDialog && (
        <AddStoreDialog
          open={openAddStoreDialog}
          onAdd={() => fetchStores()}
          onClose={() => setOpenAddStoreDialog(false)}
        />
      )}
      {openStoresDialog && (
        <StoresDialog
          open={openStoresDialog}
          stores={stores || []}
          selectedStore={stores && selectedStoreId ? stores.find(s => s._id === selectedStoreId) : null}
          refreshStores={fetchStores}
          onStoreSelect={onStoreSelect}
          onClose={() => setOpenStoresDialog(false)}
          permissions={{
            in: can(p.stores.itemIn),
            out: can(p.stores.itemOut),
            transfer: can(p.stores.transferItem),
            auditItem: can(p.stores.auditStore),
            restore: can(p.stores.itemRestore),
            delete: can(p.stores.itemDelete),
            modify: can(p.stores.modifyItemAmount),
            archive: can(p.stores.itemArchive),
            auditStore: can(p.stores.auditItem),
            CRUDItemTypes: can(p.stores.CRUDItemTypes),
            viewPrices: can(p.stores.viewStoresPrices),
          }}
        />
      )}
      {openArchivedStoresDialog && (
        <ArchivedStoresDialog
          open={openArchivedStoresDialog}
          onChange={() => fetchStores()}
          onClose={() => setOpenArchivedStoresDialog(false)}
        />
      )}
      {archiveConfirm && (
        <Dialog
          open={!!archiveConfirm}
          onClose={() => setArchiveConfirm(null)}
          maxWidth="xs"
          fullWidth>
          <DialogTitle>
            <IArchive size={20} style={{ marginRight: '8px', position: 'relative', top: '3px' }} />
            Archive Store
          </DialogTitle>
          <DialogContent>
            <div style={{ marginBottom: '8px' }}>
              Are you sure you want to archive <strong>{archiveConfirm.name}</strong>?
            </div>
            <div style={{ fontSize: '13px', color: '#888' }}>
              This store has {archiveConfirm.itemCount || 0} items.
              Items may need to be moved or removed before archiving.
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchiveConfirm(null)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const store = archiveConfirm;
                setArchiveConfirm(null);
                setArchivingId(store._id);
                try {
                  await storesMng.archiveStore(store._id);
                  toast.success('Store archived successfully', { autoClose: 900 });
                  App.eventsManager.emit('event-sites-loaded', SideMenu.name);
                } catch (e) {
                  console.error('Archive store error:', e);
                  toast.error(e?.reason || e?.message || 'Failed to archive store', { autoClose: 3000 });
                } finally {
                  setArchivingId(null);
                }
              }}
              color="secondary"
              variant="contained"
              disabled={!!archivingId}>
              Archive
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

SideMenu.defaultProps = {
  onClick: () => {},
};
SideMenu.propTypes = {
  onClick: PropTypes.func,
  value: PropTypes.number,
};
